import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { assistantService, authService } from '../utils/api';
import {
  DEEPGRAM_FIXED,
  SARVAM_DEFAULTS,
  SARVAM_LANGUAGE_OPTIONS,
  SARVAM_MODEL_OPTIONS,
  STT_PROVIDER_OPTIONS
} from '../utils/sttOptions';

const isSarvamValue = (options, value) => options.some((opt) => opt.value === value);

function SpeechToText() {
  const { assistantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assistant, setAssistant] = useState(null);
  const [form, setForm] = useState({
    stt_provider: 'deepgram',
    stt_model: '',
    stt_language: ''
  });

  const userRole = authService.getUserRole();
  const canEdit = userRole === 'super_admin' || userRole === 'admin';

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await assistantService.getAssistantById(assistantId);
        if (!isMounted) return;
        setAssistant(data);
        const provider = data.stt_provider === 'sarvam' ? 'sarvam' : 'deepgram';
        setForm({
          stt_provider: provider,
          stt_model: isSarvamValue(SARVAM_MODEL_OPTIONS, data.stt_model) ? data.stt_model : SARVAM_DEFAULTS.model,
          stt_language: isSarvamValue(SARVAM_LANGUAGE_OPTIONS, data.stt_language)
            ? data.stt_language
            : SARVAM_DEFAULTS.language
        });
      } catch (e) {
        toast.error(e?.message || 'Failed to load assistant STT settings');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    if (assistantId) load();
    return () => {
      isMounted = false;
    };
  }, [assistantId]);

  const title = useMemo(
    () => assistant?.assistant_name || assistant?.name || 'Assistant',
    [assistant]
  );

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const isSarvam = form.stt_provider === 'sarvam';

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = isSarvam
        ? { stt_provider: 'sarvam', stt_model: form.stt_model, stt_language: form.stt_language }
        : { stt_provider: 'deepgram' };

      const updated = await assistantService.updateAssistant(assistantId, payload);
      toast.success('STT settings saved');

      const selected = assistantService.getSelectedAssistant();
      if (selected?.id === assistantId) {
        assistantService.setSelectedAssistant({ ...selected, ...updated });
      }
    } catch (e) {
      toast.error(e?.message || e?.detail || 'Failed to save STT settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Speech to Text
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {title}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Card elevation={0}>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={!canEdit}>
                      <InputLabel>STT Provider</InputLabel>
                      <Select
                        value={form.stt_provider}
                        label="STT Provider"
                        onChange={(e) => handleChange('stt_provider', e.target.value)}
                      >
                        {STT_PROVIDER_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {isSarvam ? (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={!canEdit}>
                          <InputLabel>STT Model</InputLabel>
                          <Select
                            value={form.stt_model}
                            label="STT Model"
                            onChange={(e) => handleChange('stt_model', e.target.value)}
                          >
                            {SARVAM_MODEL_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={!canEdit}>
                          <InputLabel>STT Language</InputLabel>
                          <Select
                            value={form.stt_language}
                            label="STT Language"
                            onChange={(e) => handleChange('stt_language', e.target.value)}
                          >
                            {SARVAM_LANGUAGE_OPTIONS.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  ) : (
                    <>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="STT Model" value={DEEPGRAM_FIXED.model} disabled />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="STT Language" value={DEEPGRAM_FIXED.language} disabled />
                      </Grid>
                    </>
                  )}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canEdit || saving}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>

                {!canEdit && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1, display: 'block' }}
                  >
                    View-only access. Contact an admin to edit.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default SpeechToText;

